/* A single, lazily mounted color dialog for native Shopify and the local preview.
   Native <dialog> contains focus. Dawn's existing focus traps are left intact. */
(() => {
  'use strict';

  const controllerKey = Symbol.for('cellini.cardColors.initialized');
  if (window[controllerKey]) return;
  window[controllerKey] = true;

  let active = null;
  let awaitingEscapeRelease = false;

  function lockScroll() {
    // Save the longhands individually, including priority. Saving only the
    // shorthand can discard pre-existing overflow-x / overflow-y declarations.
    const saved = [document.documentElement, document.body].map((element) => ({
      element,
      properties: ['overflow-x', 'overflow-y'].map((property) => ({
        property,
        value: element.style.getPropertyValue(property),
        priority: element.style.getPropertyPriority(property),
      })),
    }));

    for (const { element, properties } of saved) {
      for (const { property } of properties) element.style.setProperty(property, 'hidden', 'important');
    }

    return () => {
      for (const { element, properties } of saved) {
        for (const { property, value, priority } of properties) {
          if (value) element.style.setProperty(property, value, priority);
          else element.style.removeProperty(property);
        }
      }
    };
  }

  function restoreAttribute(element, name, value) {
    if (value === null) element.removeAttribute(name);
    else element.setAttribute(name, value);
  }

  function dispose(state, returnFocus = true) {
    if (state.disposed) return;
    state.disposed = true;
    if (active === state) active = null;
    state.dialog.remove();
    state.unlockScroll();
    restoreAttribute(state.trigger, 'aria-expanded', state.expandedBefore);

    // A section may have been replaced while its dialog was open. Never try to
    // restore focus to a disconnected card or find another card by product ID.
    if (returnFocus && state.trigger.isConnected && !state.trigger.disabled) {
      state.trigger.focus({ preventScroll: true });
    }
  }

  function closeActive(returnFocus = true) {
    const state = active;
    if (!state) return;
    if (state.dialog.open) state.dialog.close();
    // close events are queued; synchronous cleanup keeps only one live dialog.
    dispose(state, returnFocus);
  }

  function isBackdrop(event, dialog) {
    if (event.target !== dialog) return false;
    // The panel can exceed a scrollable dialog's clipped viewport.
    const bounds = dialog.getBoundingClientRect();
    return event.clientX < bounds.left || event.clientX > bounds.right
      || event.clientY < bounds.top || event.clientY > bounds.bottom;
  }

  function openFor(trigger) {
    if (trigger.disabled || trigger.getAttribute('aria-disabled') === 'true') return;
    if (active?.trigger === trigger) return;

    const card = trigger.closest('.cellini-card');
    const template = card?.querySelector('template[data-card-colors-template]');
    const sourceDialog = template?.content.querySelector('dialog.cellini-card-colors');
    if (!sourceDialog) return;

    // Content is cloned from the trusted Liquid template. No HTML strings,
    // fetched markup, IDs, or product-identity selectors are constructed here.
    const dialog = sourceDialog.cloneNode(true);
    const panel = dialog.querySelector('.cellini-card-colors__panel');
    if (!panel || typeof dialog.showModal !== 'function') return;

    closeActive(false);
    awaitingEscapeRelease = false;
    const state = {
      dialog,
      trigger,
      expandedBefore: trigger.getAttribute('aria-expanded'),
      unlockScroll: lockScroll(),
      disposed: false,
    };
    active = state;
    let backdropPointer = null;
    let backdropClick = false;

    dialog.addEventListener('close', () => dispose(state));
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      event.stopPropagation();
      awaitingEscapeRelease = true;
      closeActive();
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') {
        const focusable = [...dialog.querySelectorAll('a[href], button, [tabindex]')].filter((element) => {
          if (element.tabIndex < 0 || element.matches(':disabled, [aria-disabled="true"]') || element.closest('[inert]')) return false;
          const visibility = getComputedStyle(element).visibility;
          return element.getClientRects().length > 0 && visibility !== 'hidden' && visibility !== 'collapse';
        });
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first) return;
        const focused = document.activeElement;
        if (event.shiftKey && (focused === first || focused === dialog)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (focused === last || focused === dialog)) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      awaitingEscapeRelease = true;
      closeActive();
    });
    dialog.addEventListener('pointerdown', (event) => {
      backdropClick = false;
      backdropPointer = event.isPrimary && event.button === 0 && isBackdrop(event, dialog)
        ? event.pointerId : null;
    });
    dialog.addEventListener('pointerup', (event) => {
      backdropClick = backdropPointer === event.pointerId && isBackdrop(event, dialog);
      backdropPointer = null;
    });
    dialog.addEventListener('pointercancel', () => {
      backdropPointer = null;
      backdropClick = false;
    });
    dialog.addEventListener('click', (event) => {
      const closeButton = event.target instanceof Element
        ? event.target.closest('[data-card-colors-close]') : null;
      if (closeButton || (backdropClick && isBackdrop(event, dialog))) {
        event.preventDefault();
        event.stopPropagation();
        closeActive();
      }
      backdropClick = false;
    });

    document.body.append(dialog);
    trigger.setAttribute('aria-expanded', 'true');
    try {
      dialog.showModal();
      // Explicit initial focus avoids scrolling to a far-down selected color.
      const initial = dialog.querySelector('[data-card-colors-close]')
        || dialog.querySelector('a[href], button:not([disabled]), [tabindex="0"]');
      initial?.focus({ preventScroll: true });
    } catch (error) {
      dispose(state);
      throw error;
    }
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('button[data-card-colors-open]');
    if (!trigger || !trigger.closest('.cellini-card')) return;
    event.preventDefault();
    openFor(trigger);
  });

  // A dialog can be opened from a recommendation inside Dawn's drawer. Once
  // focus returns there, the same Escape keyup must not also close that drawer.
  document.addEventListener('keydown', (event) => {
    if (!awaitingEscapeRelease) return;
    if (event.key !== 'Escape') {
      awaitingEscapeRelease = false;
      return;
    }
    event.preventDefault();
    event.stopPropagation();
  }, true);
  document.addEventListener('keyup', (event) => {
    if (!awaitingEscapeRelease || event.key !== 'Escape') return;
    awaitingEscapeRelease = false;
    event.stopPropagation();
  }, true);
  window.addEventListener('blur', () => { awaitingEscapeRelease = false; });
  window.addEventListener('pagehide', () => closeActive(false));
})();
